import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Loader from '../../../components/Loader';
import ModalBasic from '../../../components/Modal';
import Button from '../../../components/Button';
import PhoneField from '../../../components/PhoneField';
import { getProfileDataApi } from '../../../redux/Reducers/ProfileReducer';
import { updateProfileApi } from '../../../redux/Reducers/MyAccountReducer';
import { toast } from 'react-hot-toast';

export default function EditMyUserModal({ modalOpen, setModalOpen, data }) {
  const dispatch = useDispatch();
  const [editLoading, setEditLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    orgAdminFirstName: '',
    orgAdminLastName: '',
    orgAdminPhoneNumber: '',
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (data) {
      setFormData({
        firstName: data?.first_name || '',
        lastName: data?.last_name || '',
        email: data?.email || '',
        phoneNumber: data?.phone_numbers?.[0]?.phone_number || '',
        orgAdminFirstName: data?.org_admin_first_name || '',
        orgAdminLastName: data?.org_admin_last_name || '',
        orgAdminPhoneNumber: data?.org_admin_phone_number?.[0]?.phone_number || '',
      });
    }
  }, [data]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const errors = {};
    if (!formData.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) errors.email = 'Email is invalid';

    if (data?.account_type === 'ORGANIZATION') {
      if (!formData.orgAdminFirstName.trim()) errors.orgAdminFirstName = 'First name is required';
      if (!formData.orgAdminLastName.trim()) errors.orgAdminLastName = 'Last name is required';
      if (!formData.orgAdminPhoneNumber || formData.orgAdminPhoneNumber.length < 12)
        errors.orgAdminPhoneNumber = 'Phone number is required';
    } else {
      if (!formData.firstName.trim()) errors.firstName = 'First name is required';
      if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
      if (!formData.phoneNumber || formData.phoneNumber.length < 12) errors.phoneNumber = 'Phone number is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => {
    const payload = {};
    if (data?.account_type === 'ORGANIZATION') {
      if (formData.orgAdminFirstName !== (data?.org_admin_first_name || '')) {
        payload.org_admin_first_name = formData.orgAdminFirstName;
      }
      if (formData.orgAdminLastName !== (data?.org_admin_last_name || '')) {
        payload.org_admin_last_name = formData.orgAdminLastName;
      }
      if (formData.email !== (data?.email || '')) {
        payload.email = formData.email;
      }
      if (formData.orgAdminPhoneNumber !== (data?.org_admin_phone_number?.[0]?.phone_number || '')) {
        payload.org_admin_phone_number = { phone_number: formData.orgAdminPhoneNumber };
      }
    } else {
      if (formData.firstName !== (data?.first_name || '')) {
        payload.first_name = formData.firstName;
      }
      if (formData.lastName !== (data?.last_name || '')) {
        payload.last_name = formData.lastName;
      }
      if (formData.email !== (data?.email || '')) {
        payload.email = formData.email;
      }
      if (formData.phoneNumber !== (data?.phone_numbers?.[0]?.phone_number || '')) {
        payload.phone_number = { phone_number: formData.phoneNumber };
      }
    }

    return payload;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    setEditLoading(true);
    const payload = buildPayload();
    // If nothing changed, avoid API call
    if (Object.keys(payload).length === 0) {
      setEditLoading(false);
      toast('No changes to save');
      setModalOpen(false);
      return;
    }

    updateProfileApi(payload).then((res) => {
      setEditLoading(false);
      if ([200, 201, 204].includes(res?.status)) {
        toast.success('Profile updated successfully');
        dispatch(getProfileDataApi(false));
        setModalOpen(false);
      } else {
        toast.error(res?.message || 'Something went wrong');
      }
    });
  };

  const isOrg = data?.account_type === 'ORGANIZATION';

  return (
    <ModalBasic
      id='edit-my-user-modal'
      modalOpen={modalOpen}
      setModalOpen={setModalOpen}
      title='Edit My User'
      width='md'
    >
      <div className='z-0 rounded-lg bg-white'>
        {editLoading ? (
          <Loader text='processing...' classes='h-40' />
        ) : (
          <div className='p-6'>
            <div className='flex space-x-2'>
              <div className='relative mb-4 flex-row space-y-1.5'>
                <label className='go-label-16 mb-1 block'>First Name</label>
                <input
                  type='text'
                  className='go-inputtext-16'
                  name={isOrg ? 'orgAdminFirstName' : 'firstName'}
                  value={isOrg ? formData.orgAdminFirstName : formData.firstName}
                  onChange={handleChange}
                />
                {formErrors[isOrg ? 'orgAdminFirstName' : 'firstName'] && (
                  <p style={{ color: 'red' }}>{formErrors[isOrg ? 'orgAdminFirstName' : 'firstName']}</p>
                )}
              </div>
              <div className='relative mb-4 flex-row space-y-1.5'>
                <label className='go-label-16 mb-1 block'>Last Name</label>
                <input
                  type='text'
                  className='go-inputtext-16'
                  name={isOrg ? 'orgAdminLastName' : 'lastName'}
                  value={isOrg ? formData.orgAdminLastName : formData.lastName}
                  onChange={handleChange}
                />
                {formErrors[isOrg ? 'orgAdminLastName' : 'lastName'] && (
                  <p style={{ color: 'red' }}>{formErrors[isOrg ? 'orgAdminLastName' : 'lastName']}</p>
                )}
              </div>
            </div>
            <div className='relative mb-4 flex-row space-y-1.5'>
              <label className='go-label-16 mb-1 block'>Email</label>
              <input
                type='email'
                className='go-inputtext-16'
                name='email'
                value={formData.email}
                onChange={handleChange}
              />
              {formErrors.email && <p style={{ color: 'red' }}>{formErrors.email}</p>}
            </div>
            <div className='relative mb-4 flex-row space-y-1.5'>
              <PhoneField
                label='Phone'
                required
                name={isOrg ? 'orgAdminPhoneNumber' : 'phoneNumber'}
                value={isOrg ? formData.orgAdminPhoneNumber : formData.phoneNumber}
                onChange={handleChange}
                error={formErrors[isOrg ? 'orgAdminPhoneNumber' : 'phoneNumber']}
              />
            </div>
            <p className='pt-0 text-xs text-gray-500'>
              You agree to receive service and marketing auto-sent texts from{' '}
              {import.meta.env.VITE_TENANT_NAME === 'laundrycare' ? 'Laundry Care' : 'Push Laundry'}.
              <br />
              Opt-out anytime on your “My Account” page or text “STOP”. Message & data rates may apply.
            </p>
          </div>
        )}
        <div className='flex space-x-4 p-5'>
          <Button variant='secondary' text='Cancel' classes='w-full' onClick={() => setModalOpen(false)} />
          <Button variant='primary-dark' text='Save' classes='w-full' onClick={handleSubmit} />
        </div>
      </div>
    </ModalBasic>
  );
}
